import React, { useCallback, type RefObject } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type {
	ExtensionConfiguration,
	SelectionExtensionComponentProps,
	SelectionExtensionPlugin,
} from '@atlaskit/editor-plugin-selection-extension';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import RandomizeIcon from '@atlaskit/icon-lab/core/randomize';
import { RemixOverlay } from '@atlassian/malleable-ui-components';

const messages = defineMessages({
	remixTooltip: {
		id: 'highlight-actions.remix.inline-toolbar-tooltip',
		defaultMessage: 'Remix',
		description: 'Tooltip for the Remix button in the editor inline toolbar',
	},
	remixLabel: {
		id: 'highlight-actions.remix.inline-toolbar-label',
		defaultMessage: 'Remix',
		description: 'Label for the Remix button in the editor inline toolbar',
	},
});

/**
 * Content component rendered by the selection extension framework
 * when the Remix extension is activated via setActiveExtension.
 */
const RemixContentComponent = ({ closeExtension, selection }: SelectionExtensionComponentProps) => {
	const handleClose = useCallback(() => {
		closeExtension();
	}, [closeExtension]);

	return <RemixOverlay selectedText={selection.text} onClose={handleClose} />;
};

/**
 * Hook that returns an ExtensionConfiguration for the Remix feature
 * using the selection extension's inlineToolbar API.
 *
 * When the toolbar button is clicked, it activates the extension via
 * setActiveExtension, which triggers the SelectionExtensionComponentWrapper
 * to render RemixContentComponent (displaying the RemixOverlay modal).
 *
 * @param editorAPIRef - Ref to the editor's public plugin API
 */
export const useRemixExtension = (
	editorAPIRef: RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>,
): { extensionList: ExtensionConfiguration[] } => {
	const { formatMessage } = useIntl();

	const remixExtension: ExtensionConfiguration = {
		key: 'remix',
		source: 'first-party',
		inlineToolbar: {
			getToolbarItem: () => ({
				icon: RandomizeIcon,
				tooltip: formatMessage(messages.remixTooltip),
				label: formatMessage(messages.remixLabel),
				onClick: () => {
					const api = editorAPIRef.current;
					if (!api?.selectionExtension || !api?.core) {
						return;
					}

					const selectedText = window.getSelection()?.toString() || '';

					api.core.actions.execute(
						api.selectionExtension.commands.setActiveExtension({
							extension: {
								name: 'Remix',
								component: RemixContentComponent,
							},
							selection: {
								text: selectedText,
								from: 0,
								to: 0,
								coords: { top: 0, bottom: 0, left: 0, right: 0 },
							},
						}),
					);
				},
			}),
		},
	};

	return { extensionList: [remixExtension] };
};
